import { decode } from '@googlemaps/polyline-codec';
import { Box, CircularProgress, Dialog, Grid, Typography } from '@mui/material';
import { InfoWindowF, MarkerF, PolylineF } from '@react-google-maps/api';
import dayjs from 'dayjs';
import { Base64 } from 'js-base64';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { RoutesInfoShow } from '@/components/pages/driveRoutes/routesInfoShow';
import * as blu1Png from '@/components/shared/image/blu-1.png';
import * as blu2Png from '@/components/shared/image/blu-2.png';
import * as blu3Png from '@/components/shared/image/blu-3.png';
import * as bluGrnSvg from '@/components/shared/image/blu-grn-pin.svg';
import * as bluSvg from '@/components/shared/image/blu-pin.svg';
import * as grayPng from '@/components/shared/image/gray.png';
import * as green1Png from '@/components/shared/image/green-1.png';
import * as green2Png from '@/components/shared/image/green-2.png';
import * as green3Png from '@/components/shared/image/green-3.png';
import * as grnSvg from '@/components/shared/image/grn-pin.svg';
import { MobiTitle } from '@/components/shared/MobiTitle';
import { MbDialog } from '@/components/shared/modules';
import { queryKeys } from '@/constants/query-key';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import type { DriveRoutesParam, OperationSchedule, Waypoint } from '@/features/driveRoutes/type/operationSchedule';
import GoogleMapWrapper from '@/features/map/components/googleMapWrapper';
import MyGoogleMap from '@/features/map/components/myGoogleMap';
import { useLandscapeResize } from '@/hooks/useLandscapeResize';
import { useCreate } from '@/modules/hooks/use-create';
import { calculateMapProperties } from '@/modules/utils/coordinatesUtils';
import * as paths from '@/routes/paths';
import { settingState } from '@/state/settingState';

type SegmentType = {
  id: string;
  path: {
    lat: number;
    lng: number;
  }[];
  color: string;
};

type JWTPayload = {
  operationScheduleList: OperationSchedule[];
};

type Reservation = {
  waypointId: number; // 乗降地点ID
  waypointNumber: string; // 乗降地点番号
  waypointName: string; // 乗降地点名
  latitude: string; // 緯度
  longitude: string; // 経度
  polylineToNextWaypoint: string; // 次の地点までのポリライン
  reservationId: number; // 配車予約ID
  estimatedArrivalDatetime: string; // 到着予定日時
  isNewReservation: boolean; // 新規受託区分 true：新規受託の予約 false：既存の受託済み予約
  status: string; // ステータス
  isPickup: boolean; // 乗降区分 true：乗車 false：降車
};

type MapWaypoint = {
  waypointId: number; // 乗降地点ID
  waypointNumber: string; // 乗降地点番号
  waypointName: string; // 乗降地点名
  latitude: string; // 緯度
  longitude: string; // 経度
  polylineToNextWaypoint: string; // 次の地点までのポリライン
  reservationId: number; // 配車予約ID
  estimatedArrivalDatetime: string; // 到着予定日時
  isNewReservation: boolean; // 新規受託区分 true：新規受託の予約 false：既存の受託済み予約
  status: string; // ステータス
  isPickup: boolean; // 乗降区分 true：乗車 false：降車
  reaservationList: Reservation[];
};

/**
 * 乗降経路確認画面
 */
export const DriveRoutes: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [setting] = useRecoilState(settingState);
  const location = useLocation();
  // eslint-disable-next-line no-underscore-dangle
  const isStorybook = Boolean((globalThis as unknown as { __STORYBOOK__?: boolean }).__STORYBOOK__);

  const { areaId, searchType, checkedOfferingList, driveList } = (location.state as DriveRoutesParam) || {};

  const [driveData, setDriveData] = useState<OperationSchedule[]>([]);
  const [initLoadingOpen, setInitLoadingOpen] = useState(true);
  const [message, setMessage] = useState<string>('');
  const [showPopup, setShowPopup] = useState(false);
  const [operationScheduleDate, setOperationScheduleDate] = useState('');
  const { landscapeSplitStyle, setIsLandscapeResizing } = useLandscapeResize();

  // 乗車情報
  const [pickupInformation, setPickupInformation] = useState<MapWaypoint[]>([]);
  // 降車情報
  const [dropoffInformation, setDropoffInformation] = useState<MapWaypoint[]>([]);
  // 乗車・降車が同時に存在
  const [pickupDropoffInformation, setPickupDropoffInformation] = useState<MapWaypoint[]>([]);

  const [segments, setSegments] = useState<SegmentType[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<string>();
  const [drivercurrentLocation, setDriverCurrentLocation] = useState<google.maps.LatLngLiteral>({ lat: 0, lng: 0 });
  const [zoom, setCurrentZoom] = useState<number>(0);
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({ lat: 0, lng: 0 });

  const { mutate, isLoading } = useCreate(
    queryKeys.v1Offerings,
    ApiNamePaths.offeringsGetApi.apiName,
    ApiNamePaths.offeringsGetApi.apiPath,
  );

  const driverId = setting?.driverId || '';

  // ピンを取得する（乗車=青、降車=緑、混合=青緑2色、既存=グレー、番号1〜3表示）
  const getMarkerIcon = (mapWaypoint: MapWaypoint) => {
    let markerIcon = '';
    const { isPickup, reaservationList } = mapWaypoint;

    const numberedBlu = [blu1Png, blu2Png, blu3Png];
    const numberedGrn = [green1Png, green2Png, green3Png];

    // 新規と既存の判定
    const index = checkedOfferingList.findIndex((checkedItem) =>
      reaservationList.map((item) => String(item.reservationId)).includes(String(checkedItem.reservationId)),
    );
    // 重なった場合（番号なし）
    if (reaservationList.length > 1) {
      // 全部新規の場合、
      if (reaservationList.filter((item) => item.isNewReservation).length === reaservationList.length) {
        // 全部乗車の場合、
        if (reaservationList.filter((item) => item.isPickup).length === reaservationList.length) {
          markerIcon = bluSvg.default || '';
          // 全部降車の場合、
        } else if (reaservationList.filter((item) => !item.isPickup).length === reaservationList.length) {
          markerIcon = grnSvg.default || '';
        } else {
          markerIcon = bluGrnSvg.default || '';
        }
        // 全部既存の場合、
      } else if (reaservationList.filter((item) => !item.isNewReservation).length === reaservationList.length) {
        markerIcon = grayPng.default || '';
      } else {
        // 既存以外の新規の乗降データを取得する
        const newReaservationList = reaservationList.filter((item) => item.isNewReservation);
        if (newReaservationList.length > 1) {
          // 既存以外の全部乗車の場合、
          if (newReaservationList.filter((item) => item.isPickup).length === newReaservationList.length) {
            markerIcon = bluSvg.default || '';
            // 既存以外の全部降車の場合、
          } else if (newReaservationList.filter((item) => !item.isPickup).length === newReaservationList.length) {
            markerIcon = grnSvg.default || '';
          } else {
            markerIcon = bluGrnSvg.default || '';
          }
        } else {
          const isPickupOfNew = reaservationList.filter((item) => item.isNewReservation)[0].isPickup;
          if (index >= 0 && index <= 2) {
            markerIcon = isPickupOfNew ? numberedBlu[index].default || '' : numberedGrn[index].default || '';
          } else {
            markerIcon = isPickupOfNew ? bluSvg.default || '' : grnSvg.default || '';
          }
        }
      }
    } else {
      // 重ならない場合：
      // 既存予約の場合、
      if (index === -1) {
        markerIcon = grayPng.default || '';
      } else {
        // 新規予約の場合（番号1〜3）
        if (index >= 0 && index <= 2) {
          markerIcon = isPickup ? numberedBlu[index].default || '' : numberedGrn[index].default || '';
        } else {
          markerIcon = isPickup ? bluSvg.default || '' : grnSvg.default || '';
        }
      }
    }

    return markerIcon;
  };

  const editDriveList = useCallback(async (drives: string) => {
    const encodePayload = drives.split('.')[1];
    const decodedStr = Base64.decode(encodePayload);
    const payload = JSON.parse(decodedStr) as unknown as JWTPayload;
    const operationScheduleInfo: OperationSchedule[] = payload.operationScheduleList;

    setOperationScheduleDate(operationScheduleInfo[0]?.waypointList[0]?.estimatedArrivalDatetime);
    // ポリライン
    const newSegments: SegmentType[] = operationScheduleInfo.flatMap((operationSchedule, i) =>
      operationSchedule.waypointList.map((waypoint, index) => {
        const decodedPath = decode(waypoint.polylineToNextWaypoint, 5);

        return {
          id: `${i}${index}`,
          path: decodedPath.map(([lat, lng]) => ({ lat, lng })),
          color: 'blue',
        };
      }),
    );
    setSegments(newSegments);

    const sortWaypoints = (waypoints: Waypoint[]) =>
      waypoints.sort((a, b) => {
        const dateComparison = a.estimatedArrivalDatetime.localeCompare(b.estimatedArrivalDatetime);
        if (dateComparison === 0) {
          return a.isPickup === b.isPickup ? 0 : a.isPickup ? 1 : -1;
        }

        return dateComparison;
      });

    const sortSchedules = (schedules: OperationSchedule[]) =>
      schedules
        .map((schedule) => ({
          ...schedule,
          waypointList: sortWaypoints(schedule.waypointList),
        }))
        .sort((a, b) =>
          a.waypointList[0].estimatedArrivalDatetime.localeCompare(b.waypointList[0].estimatedArrivalDatetime),
        );

    // 到着予定日時でソートする
    const sortedData = sortSchedules(operationScheduleInfo);

    // 画面左側用データ
    setDriveData(sortedData);

    const extractWaypointsInfo = (scheduleList: OperationSchedule[]) => {
      const waypointMap: {
        [waypointId: number]: { isPickup: boolean; isDropoff: boolean; waypointInfo: MapWaypoint };
      } = {};

      scheduleList.forEach((schedule) => {
        schedule.waypointList.forEach((waypoint) => {
          const { waypointId, isPickup } = waypoint;

          if (!waypointMap[waypointId]) {
            waypointMap[waypointId] = {
              isPickup: false,
              isDropoff: false,
              waypointInfo: { ...waypoint, reaservationList: [] },
            };
          }

          if (isPickup) {
            waypointMap[waypointId].isPickup = true;
            waypointMap[waypointId].waypointInfo.reaservationList.push({
              ...waypoint,
            });
          } else {
            waypointMap[waypointId].isDropoff = true;
            waypointMap[waypointId].waypointInfo.reaservationList.push({
              ...waypoint,
            });
          }
        });
      });

      const pickupPoints: MapWaypoint[] = [];
      const dropoffPoints: MapWaypoint[] = [];
      const bothPoints: MapWaypoint[] = [];

      Object.values(waypointMap).forEach(({ isPickup, isDropoff, waypointInfo }) => {
        if (isPickup && isDropoff) {
          bothPoints.push(waypointInfo);
        } else if (isPickup) {
          pickupPoints.push(waypointInfo);
        } else if (isDropoff) {
          dropoffPoints.push(waypointInfo);
        }
      });

      return { pickupPoints, dropoffPoints, bothPoints };
    };

    // // 降車・乗車が同時に存在の場合、到着予定日時でノート、到着予定日時が同じの場合、降車が優先に表示
    const sortBothWaypoints = (waypoints: MapWaypoint[]): MapWaypoint[] =>
      waypoints.map((waypointInfo) => {
        waypointInfo.reaservationList.sort((a, b) => {
          const dateComparison = a.estimatedArrivalDatetime.localeCompare(b.estimatedArrivalDatetime);
          if (dateComparison === 0) {
            return a.isPickup === b.isPickup ? 0 : a.isPickup ? 1 : -1;
          }

          return dateComparison;
        });

        return waypointInfo;
      });

    // 降車・乗車の場合、到着予定日時でノート
    const sortPickUpOrDropOffWaypoints = (waypoints: MapWaypoint[]): MapWaypoint[] =>
      waypoints.map((waypointInfo) => {
        waypointInfo.reaservationList.sort((a, b) =>
          a.estimatedArrivalDatetime.localeCompare(b.estimatedArrivalDatetime),
        );

        return waypointInfo;
      });

    const { pickupPoints, dropoffPoints, bothPoints } = extractWaypointsInfo(operationScheduleInfo);

    // Marker用
    setPickupInformation(sortPickUpOrDropOffWaypoints(pickupPoints));
    setDropoffInformation(sortPickUpOrDropOffWaypoints(dropoffPoints));
    setPickupDropoffInformation(sortBothWaypoints(bothPoints));
  }, []);

  useEffect(() => {
    if (driveList) {
      void editDriveList(driveList);
    }
  }, [driveList, editDriveList]);

  const truncateToSixDecimal = (latitudeLongitude: number) => {
    const decimalIndex = String(latitudeLongitude).indexOf('.');
    if (decimalIndex === -1) {
      return latitudeLongitude;
    }

    return Number(String(latitudeLongitude).substring(0, decimalIndex + 7));
  };

  const getDriverCurrentLocation = useCallback(() => {
    // 初期処理でGPS情報からアプリ利用者の現在地を取得する
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setDriverCurrentLocation({
          lat: truncateToSixDecimal(position.coords.latitude),
          lng: truncateToSixDecimal(position.coords.longitude),
        });
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('現在地取得エラー:', err);
      },
    );
  }, []);

  useEffect(() => {
    getDriverCurrentLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDriverCurrentLocation]);

  useEffect(() => {
    // 縮尺、中心を取得
    if (pickupInformation.length > 0 || dropoffInformation.length > 0 || pickupDropoffInformation.length > 0) {
      const pickupInfo = pickupInformation.map((item) => ({
        lat: Number(item?.latitude),
        lng: Number(item?.longitude),
      }));
      const dropoffInfo = dropoffInformation.map((item) => ({
        lat: Number(item?.latitude),
        lng: Number(item?.longitude),
      }));
      const bothInfo = pickupDropoffInformation.map((item) => ({
        lat: Number(item?.latitude),
        lng: Number(item?.longitude),
      }));

      const locationInfo = [...pickupInfo, ...dropoffInfo, ...bothInfo];

      const { zooms, centers } = calculateMapProperties(locationInfo);
      if (zooms && centers) {
        setCenter(centers);
        setCurrentZoom(zooms);
        setInitLoadingOpen(false);
      }
    }
  }, [pickupInformation, dropoffInformation, pickupDropoffInformation]);

  const formatDate = (datetimeStr: string) => {
    if (!datetimeStr) return '';

    return dayjs(datetimeStr).format('M月D日（ddd）');
  };

  const convertTimeFormat = (dateTimeString: string) => {
    if (!dateTimeString) return '';

    return dayjs(dateTimeString).format('HH:mm');
  };

  // 「確定」ボタン押下
  const handleConfirm = () => {
    const body = {
      driverId,
      optimizedRoutes: driveList,
      isNotification: true,
    };

    mutate(body, {
      onSuccess: () => {
        setShowPopup(true);
        setMessage(t('driveRoutes.entrustedSuccessMessage'));
      },
      onError: () => {
        setShowPopup(true);
        setMessage(t('driveRoutes.entrustedFailMessage'));
      },
    });
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    navigate(paths.offering, { state: { areaId, searchType } });
  };

  const onMarkerClick = (markerId: string) => {
    setSelectedMarker(markerId);
  };

  const renderMarkers = () => {
    // 乗車地点を表示
    const pickupInformationMarkers = pickupInformation.map((markerData) => (
      <MarkerF
        key={markerData.waypointId}
        position={{ lat: Number(markerData.latitude), lng: Number(markerData.longitude) }}
        icon={{
          url: getMarkerIcon(markerData),
          scaledSize: new google.maps.Size(35, 50),
        }}
        onClick={() => onMarkerClick(markerData.waypointId.toString())}
      >
        {' '}
        {selectedMarker === markerData.waypointId.toString() && (
          <InfoWindowF
            position={{ lat: Number(markerData.latitude), lng: Number(markerData.longitude) }}
            onCloseClick={() => setSelectedMarker('')}
          >
            <Box sx={{ width: '300px', pt: 2, pb: 1 }}>
              {markerData.reaservationList.map((item) => (
                <Grid
                  key={`${item.waypointId},${item.reservationId}`}
                  container
                  sx={{
                    wordBreak: 'break-all',
                  }}
                >
                  <Grid item xs={4}>
                    <Typography align="center" sx={{ fontWeight: '600', fontSize: '20px' }}>
                      {t('driveRoutes.pickupDatetime')}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography align="center" sx={{ fontSize: '20px' }}>
                      {convertTimeFormat(item.estimatedArrivalDatetime)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography align="center" sx={{ fontWeight: '600', fontSize: '20px' }}>
                      {t('driveRoutes.pickupWaypointName')}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography align="center" sx={{ fontSize: '20px' }}>
                      {`${item.waypointNumber}. ${item.waypointName}`}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </InfoWindowF>
        )}
      </MarkerF>
    ));

    // 降車地点を表示
    const dropoffInformationMarkers = dropoffInformation.map((markerData) => (
      <MarkerF
        key={markerData.waypointId}
        position={{ lat: Number(markerData.latitude), lng: Number(markerData.longitude) }}
        icon={{
          url: getMarkerIcon(markerData),
          scaledSize: new google.maps.Size(35, 50),
        }}
        onClick={() => onMarkerClick(markerData.waypointId.toString())}
      >
        {' '}
        {selectedMarker === markerData.waypointId.toString() && (
          <InfoWindowF
            position={{ lat: Number(markerData.latitude), lng: Number(markerData.longitude) }}
            onCloseClick={() => setSelectedMarker('')}
          >
            <Box sx={{ width: '300px', pt: 2, pb: 1 }}>
              {markerData.reaservationList.map((item) => (
                <Grid
                  key={`${item.waypointId},${item.reservationId}`}
                  container
                  sx={{
                    wordBreak: 'break-all',
                  }}
                >
                  <Grid item xs={4}>
                    <Typography align="center" sx={{ fontWeight: '600', fontSize: '20px' }}>
                      {t('driveRoutes.dropoffDatetime')}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography align="center" sx={{ fontSize: '20px' }}>
                      {convertTimeFormat(item.estimatedArrivalDatetime)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography align="center" sx={{ fontWeight: '600', fontSize: '20px' }}>
                      {t('driveRoutes.dropoffWaypointName')}
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography align="center" sx={{ fontSize: '20px' }}>
                      {`${item.waypointNumber}. ${item.waypointName}`}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </InfoWindowF>
        )}
      </MarkerF>
    ));

    // 乗車降車が被った地点を表示
    const pickupDropoffInformationMarkers = pickupDropoffInformation.map((markerData) => (
      <MarkerF
        key={markerData.waypointId}
        position={{ lat: Number(markerData.latitude), lng: Number(markerData.longitude) }}
        icon={{
          url: getMarkerIcon(markerData),
          scaledSize: new google.maps.Size(35, 50),
        }}
        onClick={() => onMarkerClick(markerData.waypointId.toString())}
      >
        {' '}
        {selectedMarker === markerData.waypointId.toString() && (
          <InfoWindowF
            position={{ lat: Number(markerData.latitude), lng: Number(markerData.longitude) }}
            onCloseClick={() => setSelectedMarker('')}
          >
            <Box sx={{ width: '300px', pt: 2, pb: 1 }}>
              {markerData.reaservationList.map((item) => (
                <Grid
                  key={`${item.waypointId},${item.reservationId}`}
                  container
                  sx={{
                    wordBreak: 'break-all',
                  }}
                >
                  {item.isPickup ? (
                    <>
                      <Grid item xs={4}>
                        <Typography align="center" sx={{ fontWeight: '600', fontSize: '20px' }}>
                          {t('driveRoutes.pickupDatetime')}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography align="center" sx={{ fontSize: '20px' }}>
                          {convertTimeFormat(item.estimatedArrivalDatetime)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography align="center" sx={{ fontWeight: '600', fontSize: '20px' }}>
                          {t('driveRoutes.pickupWaypointName')}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography align="center" sx={{ fontSize: '20px' }}>
                          {`${item.waypointNumber}. ${item.waypointName}`}
                        </Typography>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={4}>
                        <Typography align="center" sx={{ fontWeight: '600', fontSize: '20px' }}>
                          {t('driveRoutes.dropoffDatetime')}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography align="center" sx={{ fontSize: '20px' }}>
                          {convertTimeFormat(item.estimatedArrivalDatetime)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography align="center" sx={{ fontWeight: '600', fontSize: '20px' }}>
                          {t('driveRoutes.dropoffWaypointName')}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography align="center" sx={{ fontSize: '20px' }}>
                          {`${item.waypointNumber}. ${item.waypointName}`}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              ))}
            </Box>
          </InfoWindowF>
        )}
      </MarkerF>
    ));

    return [...pickupInformationMarkers, ...dropoffInformationMarkers, ...pickupDropoffInformationMarkers].filter(
      Boolean,
    ); // リストからnullやfalseなどのfalsyな値を除外
  };

  return (
    <div className="u-flex-fill" style={{ flexDirection: 'column' }}>
      <MobiTitle>{t('driveRoutes.title')}</MobiTitle>
      {/* 左右分割レイアウト。表示範囲変更機能のため削除しないこと。 */}
      <div className="drive-routes-split-layout" style={landscapeSplitStyle}>
        <div className="drive-routes-info-panel">
          <div className="drive-routes-info-panel-inner">
            <div className="u-pl-20 u-pr-20 u-py-5 u-border-bottom-strong u-text-20">
              <div>
                {t('driveRoutes.pickupDate')}：{formatDate(operationScheduleDate)}
              </div>
            </div>
            <div className="u-pl-20 u-pr-20">
              <RoutesInfoShow driveList={driveData} />
            </div>
            <div className="group-driveRoutes">
              <button
                type="button"
                className="primary-action"
                disabled={isLoading}
                onClick={() => handleConfirm()}
                style={{ width: '80%', margin: '0 auto' }}
              >
                {t('driveRoutes.confirm')}
              </button>
            </div>
            <div className="group-driveRoutes u-py-5">
              <button
                type="button"
                className="secondary-action"
                onClick={() => {
                  navigate(paths.waypointConfirm, { state: { areaId, searchType, checkedOfferingList } });
                }}
                style={{ width: '80%', margin: '0 auto' }}
              >
                {t('driveRoutes.back')}
              </button>
            </div>

            <MbDialog
              open={showPopup}
              content={message}
              okLabel="OK"
              onOk={() => {
                handleClosePopup();
              }}
            />
          </div>
        </div>
        {/* 左右リサイズハンドル。表示範囲変更機能のため削除しないこと。 */}
        <button
          type="button"
          className="landscape-resize-handle"
          onMouseDown={() => setIsLandscapeResizing(true)}
          onTouchStart={() => setIsLandscapeResizing(true)}
          aria-label="Resize info and map panels"
        >
          <span className="landscape-resize-handle-bar" />
        </button>
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Dialog
            open={initLoadingOpen}
            sx={{
              '.MuiDialog-paper': {
                boxShadow: 'none',
                backgroundColor: 'transparent',
                overflow: 'hidden',
              },
            }}
          >
            <CircularProgress sx={{ margin: '0 auto' }} />
          </Dialog>
          {isStorybook ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#f2f2f2',
                color: '#6f6f6f',
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              Map preview is disabled in Storybook
            </Box>
          ) : !initLoadingOpen ? (
            <GoogleMapWrapper>
              <MyGoogleMap center={center} zoom={zoom} onMarkerClick={onMarkerClick} renderMarkers={renderMarkers}>
                {segments.map((segment: SegmentType) => (
                  <PolylineF
                    key={segment.id}
                    path={segment.path}
                    options={{
                      strokeColor: segment.color,
                      strokeOpacity: 1,
                      strokeWeight: 5,
                      clickable: false,
                      draggable: false,
                      editable: false,
                      visible: true,
                      zIndex: 1,
                    }}
                  />
                ))}
                {typeof window.google !== 'undefined' &&
                typeof window.google.maps !== 'undefined' &&
                typeof window.google.maps.SymbolPath !== 'undefined' &&
                drivercurrentLocation.lat !== 0 &&
                drivercurrentLocation.lng !== 0 ? (
                  <MarkerF
                    key="currentLocation"
                    position={drivercurrentLocation}
                    icon={{
                      path: google.maps.SymbolPath.CIRCLE,
                      scale: 12,
                      fillColor: '#4285F4',
                      fillOpacity: 0.9,
                      strokeWeight: 2,
                      strokeColor: 'white',
                    }}
                    zIndex={google.maps.Marker.MAX_ZINDEX + 1}
                    title="現在地"
                  />
                ) : null}
                ;
              </MyGoogleMap>
            </GoogleMapWrapper>
          ) : null}
        </Box>
      </div>
    </div>
  );
};
